import { Button, CopyButton, Menu, Tooltip } from '@mantine/core';
import { modals } from '@mantine/modals';
import {
  IconCheck,
  IconChevronDown,
  IconCopy,
  IconExclamationCircle,
  IconExternalLink,
  IconFileDollar,
  IconInvoice,
  IconLayersIntersect2,
  IconListCheck,
  IconMailShare,
  IconPlayerPause,
  IconPlayerPlay,
  IconPlus,
  IconSend,
  IconTrash,
  IconUser,
  IconWallet,
} from '@tabler/icons-react';
import { debtorAtom } from 'app/Private/Clients/Detail/client-atom';
import { useDebtorRecoveryActions } from 'app/Private/Clients/hooks/use-debtor-recovery-actions';
import { PageTitle } from 'components/PageTitle';
import { SuperButton } from 'components/SuperButton';
import { useCompanyConfiguration } from 'init/use-company-configuration';
import { useAtomValue } from 'jotai';
import { i18nKeys, tKeys, tKeysLoose, useTranslation } from 'locales';
import { useCanSendMail } from 'modules/Mails/hooks/use-can-send-mail';
import { NewMail } from 'modules/Mails/pages/NewMail/NewMail';
import { TaskForm } from 'modules/Tasks';
import { useState } from 'react';
import { useHistory } from 'react-router';
import AYSModal from 'shared/components/AYSModal';
import { NEW_PORTAL_FEATURE_FLAG } from 'shared/constants';
import { useSendClientPortalNotification } from 'shared/hooks/use-send-client-portal-notification';
import { ButtonColor } from 'shared/io';
import { useCanUseManualActions } from 'shared/utils/connector';
import { isDevelopment } from 'shared/utils/environment';
import { useIsFeatureFlagEnabled } from 'shared/utils/feature-flags';
import { clientActions } from 'store/client/client.actions';
import { DialogShowId, DialogShowSize, showDialog } from 'store/view/view.actions';
import { SelectDuplicateDebtorForm } from '../Duplicates/SelectDuplicateDebtorForm';

export const ClientHeader = () => {
  const { t } = useTranslation();
  const company = useCompanyConfiguration();
  const history = useHistory();

  const { sendClientPortalNotification, isSendClientPortalNotificationLoading } =
    useSendClientPortalNotification();
  const { canUseExternalMail } = useCanSendMail();
  const { debtor, refetch } = useAtomValue(debtorAtom);
  const isNewPortalEnabled = useIsFeatureFlagEnabled(NEW_PORTAL_FEATURE_FLAG);
  const portalLink = isNewPortalEnabled ? debtor.portal_link : debtor.debtor_reaction_link;
  const { isPaused, isDisputed, togglePause, toggleDispute } = useDebtorRecoveryActions({
    debtor,
    onSuccess: () => {
      clientActions.detail({ id: debtor.id });
      refetch();
    },
  });

  const [hasClickedPortalNotification, setHasClickedPortalNotification] = useState(false);

  const hasManualActionsActive = useCanUseManualActions();

  const handleDelete = () => {
    showDialog({
      id: DialogShowId.CONFIRM,
      size: DialogShowSize.SMALL,
      title: t(i18nKeys.CONFIRM),
      keepMountOnExit: true,
      children: (
        <AYSModal
          text={t(i18nKeys.CLIENT.AYS_TO_DELETE_CLIENT)}
          confirmButtonColor={ButtonColor.RED}
          confirmButtonText={t(i18nKeys.DELETE)}
          onConfirm={() =>
            clientActions.delete({
              id: debtor.id,
              callback: () => {
                history.push('/clients');
              },
            })
          }
        />
      ),
    });
  };

  const handleSendClientPortalNotification = () => {
    sendClientPortalNotification(
      { debtorId: debtor.id },
      { onSuccess: () => setHasClickedPortalNotification(true) },
    );
  };

  const handleNewEmail = () => {
    modals.open({
      title: t(i18nKeys.EXTERNAL_MAIL.COMPOSE_TITLE),
      size: 'auto',
      centered: true,
      children: <NewMail debtorMail={debtor.emails[0]} debtorId={String(debtor.id)} />,
    });
  };

  const handleMerge = () => {
    showDialog({
      id: DialogShowId.CUSTOM,
      size: DialogShowSize.SMALL,
      title: t(i18nKeys.INVOICING.CREATE.SELECT_CLIENT),
      children: <SelectDuplicateDebtorForm currentDebtor={debtor} />,
    });
  };

  const create = (type: any) => () => {
    history.push(`/${type}/new?debtor_id=${debtor.id}`);
  };

  const addTask = () => {
    modals.open({
      title: t(i18nKeys.FORM.TASK.NEW),
      size: 'xl',
      children: <TaskForm debtorId={debtor.id.toString()} />,
    });
  };

  const addPaymentPlan = () => history.push(`/payment-plans/new?debtor_id=${debtor.id}`);

  const addContactPerson = () => history.push('contact-persons/new');

  return (
    <PageTitle>
      <PageTitle.Back />
      {debtor.full_name}
      {/* This value can be empty if there are no invoices yet. The reason is that historically,
          the token required for the debtor to access the payment portal was stored on the invoice
          Now that multiple invoices can exist for one client, the link is generated with the token of that last open invoice, if any */}
      {portalLink && (
        <CopyButton value={portalLink}>
          {({ copied, copy }) => (
            <Button.Group>
              <Tooltip label={t(i18nKeys.CLIENT.TOOLTIP.COPY_PORTAL_LINK)}>
                <Button
                  size="compact-md"
                  leftSection={copied ? <IconCheck size={18} /> : <IconCopy size={18} />}
                  variant="default"
                  onClick={copy}
                >
                  {copied ? t(i18nKeys.COPIED_LINK) : t(i18nKeys.CLIENT.PORTAIL_LINK)}
                </Button>
              </Tooltip>
              <Tooltip label={t(i18nKeys.CLIENT.TOOLTIP.OPEN_PORTAL_NEW_TAB)}>
                <Button
                  px={10}
                  size="compact-md"
                  variant="default"
                  onClick={() => window.open(portalLink, '_blank')}
                >
                  <IconExternalLink stroke={1.5} size={20} />
                </Button>
              </Tooltip>
              <Tooltip
                arrowPosition="side"
                label={
                  hasClickedPortalNotification
                    ? t(i18nKeys.CLIENT.TOOLTIP.EMAIL_SENT)
                    : `${t(i18nKeys.CLIENT.TOOLTIP.SEND_PORTAL_EMAIL)}`
                }
              >
                <Button
                  px={10}
                  size="compact-md"
                  variant="default"
                  loading={isSendClientPortalNotificationLoading}
                  disabled={hasClickedPortalNotification}
                  onClick={() => handleSendClientPortalNotification()}
                >
                  <IconMailShare stroke={1.5} size={20} />
                </Button>
              </Tooltip>
            </Button.Group>
          )}
        </CopyButton>
      )}
      {isDevelopment() && debtor.debtor_reaction_link && (
        <Button
          component="a"
          href={debtor.debtor_reaction_link}
          target="_blank"
          size="compact-md"
          variant="light"
          color="orange"
          rightSection={<IconExternalLink stroke={1.5} size={18} />}
        >
          Legacy portal
        </Button>
      )}
      <PageTitle.Actions>
        <SuperButton
          onClick={handleDelete}
          disabledMessage={
            debtor.opened_cases_count > 0 ? t('SUPERADMIN.WARNING_MESSAGES.INVOICE') : null
          }
        >
          {t(i18nKeys.DELETE)}
        </SuperButton>
        <Menu withArrow>
          <Menu.Target>
            <Button variant="default" rightSection={<IconPlus size={20} stroke={1.5} />}>
              {t(tKeys.ACTIONS.ADD)}
            </Button>
          </Menu.Target>
          <Menu.Dropdown>
            {hasManualActionsActive && (
              <>
                <Menu.Item
                  leftSection={<IconFileDollar size={18} stroke={1.5} />}
                  onClick={create('invoices')}
                >
                  {t(i18nKeys.COMMON.INVOICE)}
                </Menu.Item>
                <Menu.Item
                  leftSection={<IconInvoice size={18} stroke={1.5} />}
                  onClick={create('credit-notes')}
                >
                  {t(i18nKeys.CREDIT_NOTE)}
                </Menu.Item>
              </>
            )}
            {canUseExternalMail && (
              <Menu.Item leftSection={<IconSend size={16} stroke={1.5} />} onClick={handleNewEmail}>
                {t(i18nKeys.EXTERNAL_MAIL.NEW)}
              </Menu.Item>
            )}
            {company.package.can_use_tasks && (
              <Menu.Item leftSection={<IconListCheck size={18} stroke={1.5} />} onClick={addTask}>
                {t(i18nKeys.COMMON.TASK)}
              </Menu.Item>
            )}
            {company.package.can_use_payment_plans && (
              <Menu.Item
                leftSection={<IconWallet size={18} stroke={1.5} />}
                onClick={addPaymentPlan}
              >
                {t(i18nKeys.PAYMENT_PLAN.TITLE.ONE)}
              </Menu.Item>
            )}
            <Menu.Item leftSection={<IconUser size={18} stroke={1.5} />} onClick={addContactPerson}>
              {t(i18nKeys.CONTACT_PERSON.ONE)}
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
        <Menu withArrow position="bottom-end">
          <Menu.Target>
            <Button variant="default" rightSection={<IconChevronDown size={20} stroke={1.5} />}>
              {t(i18nKeys.FORM.ACTIONS)}
            </Button>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Item
              leftSection={
                isPaused ? (
                  <IconPlayerPlay size={18} stroke={1.5} />
                ) : (
                  <IconPlayerPause size={18} stroke={1.5} />
                )
              }
              disabled={isDisputed}
              onClick={togglePause}
            >
              {isPaused
                ? t(i18nKeys.CLIENT.DETAIL.UNPAUSE_DEBTOR_RECOVERY)
                : t(i18nKeys.CLIENT.DETAIL.PAUSE_DEBTOR_RECOVERY)}
            </Menu.Item>
            <Menu.Item
              leftSection={<IconExclamationCircle size={18} stroke={1.5} />}
              disabled={isPaused}
              onClick={toggleDispute}
            >
              {t(tKeysLoose[isDisputed ? 'UNDISPUTED' : 'MARK_AS_DISPUTED'])}
            </Menu.Item>
            <Menu.Item
              leftSection={<IconLayersIntersect2 size={18} stroke={1.5} />}
              onClick={handleMerge}
            >
              {t(i18nKeys.MERGE_CLIENT)}
            </Menu.Item>
            <Menu.Item
              leftSection={<IconTrash size={18} stroke={1.5} />}
              color="red"
              onClick={handleDelete}
            >
              {t(i18nKeys.DELETE)}
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </PageTitle.Actions>
    </PageTitle>
  );
};
